import React, { useEffect, useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useAuth } from '../contexts/AuthContext'

type TemplateTab = 'overview' | 'builder' | 'admin'

interface AppTemplatePageProps {
}

const AppTemplatePage: React.FC<AppTemplatePageProps> = () => {
  const { loading: authLoading, hasPermission } = useAuth()

  const showAdmin = hasPermission('templateManager')
  const [tab, setTab] = useState<TemplateTab>('overview')

  useEffect(() => {
    if (tab === 'admin' && !showAdmin) setTab('overview')
  }, [showAdmin, tab])

  // Note: RequirePermission wrapper may prevent rendering while authLoading=true.
  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    )
  }

  const handleTabChange = (next: TemplateTab) => {
    if (next === 'admin' && !showAdmin) return
    setTab(next)
  }

  const news = ['Template app ready', 'Add your app-specific logic next', 'Permissions drive admin access']
  const recentActivity = ['System template updated', 'New app scaffolding available', 'Next: connect APIs']

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="TEMPLATE" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1">
        <div className="tabs tabs-lift mb-4">
          <input
            type="radio"
            name="template_tabs"
            role="tab"
            className="tab"
            aria-label="Overview"
            checked={tab === 'overview'}
            onChange={() => handleTabChange('overview')}
          />
          <input
            type="radio"
            name="template_tabs"
            role="tab"
            className="tab"
            aria-label="Builder"
            checked={tab === 'builder'}
            onChange={() => handleTabChange('builder')}
          />
          {showAdmin && (
            <input
              type="radio"
              name="template_tabs"
              role="tab"
              className="tab"
              aria-label="Admin"
              checked={tab === 'admin'}
              onChange={() => handleTabChange('admin')}
            />
          )}
        </div>

        <div>
          {tab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">Overview</h2>
                  <p className="text-base-content/70">
                    This is a starter scaffold for new “apps” inside the portal. Keep pages thin; move API workflow and
                    integration logic into shared services.
                  </p>
                  <div className="card-actions mt-4">
                    <button className="btn btn-primary" onClick={() => setTab('builder')}>
                      Go to Builder
                    </button>
                  </div>
                </div>
              </div>
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">Permission Model</h2>
                  <ul className="list-disc pl-5 text-sm text-base-content/70 space-y-2">
                    <li>
                      Page access is controlled by permission key: <span className="font-mono">template</span>
                    </li>
                    <li>
                      Admin tab is controlled by permission key: <span className="font-mono">templateManager</span>
                    </li>
                  </ul>
                  <div className="mt-4 text-xs text-base-content/50">
                    Update these keys when duplicating the template for a real app.
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'builder' && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Builder</h2>
                <p className="text-base-content/70">
                  Add your app UI here. Prefer calling centralized frontend services (wrapping backend endpoints),
                  and let the backend enforce RBAC for integration endpoints.
                </p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-base-200 rounded-xl p-4">
                    <div className="font-semibold mb-1">Next steps</div>
                    <div className="text-sm text-base-content/70 space-y-2">
                      <div>1. Duplicate this page</div>
                      <div>2. Create shared services for your app APIs</div>
                      <div>3. Gate UI with permission keys</div>
                    </div>
                  </div>
                  <div className="bg-base-200 rounded-xl p-4">
                    <div className="font-semibold mb-1">Thin page guidance</div>
                    <div className="text-sm text-base-content/70">
                      Keep state management local, but move API calls into `src/services/*` so every page stays
                      consistent and testable.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'admin' && showAdmin && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Admin</h2>
                <p className="text-base-content/70">
                  This tab is only visible when the current user has <span className="font-mono">templateManager</span>.
                </p>
                <div className="mt-6">
                  <div className="alert alert-info">
                    Replace this section with your app’s admin/management UI.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default AppTemplatePage

